import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  Edit2,
  Loader2,
  Link as LinkIcon,
  Upload,
  Save,
  X,
  Camera,
  Building2,
  Shield,
  CheckCircle2,
  Clock,
  XCircle,
  ClipboardList,
  TrendingUp,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import { cn } from "@/lib/utils";
import { resolveMediaUrl, useImageBlobUrl, isAzureSasUrl } from "@/lib/media";
import { useAgentePermissions } from "@/hooks/useAgentePermissions";
import { useCurrentAgenteStore } from "@/stores/currentAgenteStore";
import { AppConfig } from "@/datasources/AppConfig";
import { OUVIDORIA_ENDPOINTS } from "@/config";
import { decryptId } from "@/utils/tokenCrypto";

// Normaliza: remove acentos, espaços, underscores e lowercase.
// (Mesma estratégia usada em SolicitacoesPage.)
const normalizeValue = (v?: string | null) =>
  (v || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s_-]+/g, "")
    .toLowerCase();

interface FotoPerfil {
  id: string;
  arquivo: string;
}

interface Agente {
  id: string;
  nome: string;
  email?: string | null;
  telefone?: string | null;
  fotoPerfil?: FotoPerfil | null;
  intergracao?: string | null;
  arquivado: boolean;
  gestor?: boolean;
}

interface SolicitacaoAgente {
  id: string;
  status?: string | null;
  estadoAprovacao?: string | null;
}

export default function PerfilPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { agente: currentAgente, status: agenteStatus, isReadOnly } = useAgentePermissions();
  const fetchCurrentAgente = useCurrentAgenteStore((s) => s.fetchCurrentAgente);

  const [agente, setAgente] = useState<Agente | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [editForm, setEditForm] = useState({
    nome: "",
    email: "",
    telefone: "",
    fotoPerfil: "",
  });

  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoAgente[]>([]);
  const [isLoadingSolicitacoes, setIsLoadingSolicitacoes] = useState(false);
  const [ouvidoriaNome, setOuvidoriaNome] = useState<string | null>(null);
  const [agenteId, setAgenteId] = useState<string>(AppConfig.agenteId || '');
  const [fotoVersion, setFotoVersion] = useState(0);

  // Carregar agenteId do localStorage se não estiver no AppConfig
  useEffect(() => {
    const loadAgenteId = async () => {
      if (!AppConfig.agenteId) {
        const agenteIdCifrado = localStorage.getItem('agenteId');
        if (agenteIdCifrado) {
          const decryptedAgenteId = await decryptId(agenteIdCifrado);
          if (decryptedAgenteId) {
            AppConfig.agenteId = decryptedAgenteId;
            setAgenteId(decryptedAgenteId);
          }
        }
      } else {
        setAgenteId(AppConfig.agenteId);
      }
    };
    loadAgenteId();
  }, []);

  const fetchAgente = async () => {
    if (!agenteId) {
      setError(
        "Agente não encontrado. Faça login novamente."
      );
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      const endpoint = `v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes/${agenteId}`;
      
      const result = await dataSource.get<Record<string, unknown>>({
        endpoint,
        headers: { Accept: "application/vnd.data.full.v1+json" },
        forceRefresh: true,
      });

      if ("failure" in result) {
        const errorMsg =
          result.failure.messages?.join(", ") || "Erro ao carregar perfil";
        setError(errorMsg);
        return;
      }

      const raw = result.success.body as Record<string, unknown>;

      if (!raw) {
        setError("Agente não encontrado na ouvidoria atual");
        return;
      }

      // A API pode retornar um array, então pegamos o primeiro elemento
      const agenteRaw = Array.isArray(raw) ? raw[0] : raw;

      if (!agenteRaw) {
        setError("Agente não encontrado na ouvidoria atual");
        return;
      }

      const agenteData: Agente = {
        id: String(agenteRaw.id || ""),
        nome: String(agenteRaw.nome || ""),
        email: (agenteRaw.email as string) || null,
        telefone: (agenteRaw.telefone as string) || null,
        fotoPerfil: (() => {
          const fp = agenteRaw.fotoPerfil;
          if (!fp) return null;
          if (typeof fp === "string") {
            return { id: "", arquivo: fp };
          }
          const obj = fp as Record<string, unknown>;
          return {
            id: (obj.id as string) || "",
            arquivo: (obj.arquivo as string) || "",
          };
        })(),
        intergracao: (agenteRaw.intergracao as string) || null,
        arquivado: Boolean(agenteRaw.arquivado),
        gestor: Boolean(agenteRaw.gestor),
      };

      setAgente(agenteData);
      setEditForm({
        nome: agenteData.nome,
        email: agenteData.email || "",
        telefone: agenteData.telefone || "",
        fotoPerfil: agenteData.fotoPerfil?.arquivo || "",
      });
    } catch (err) {
      setError(
        "Erro ao carregar dados do perfil: " +
          (err instanceof Error ? err.message : String(err))
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSolicitacoes = async () => {
    if (!agenteId) return;
    setIsLoadingSolicitacoes(true);
    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      const pageSize = 500;
      const allItems: SolicitacaoAgente[] = [];
      let page = 1;

      while (true) {
        const result = await dataSource.get<SolicitacaoAgente[]>({
          endpoint: `solicitacoes`,
          headers: { Accept: "application/vnd.data.partial.v2+json" },
          queryParams: {
            agenteId,
            ouvidoriaId: AppConfig.ouvidoriaId,
            PageNumber: page,
            PageSize: pageSize,
          },
          forceRefresh: true,
          serialize: (data) => {
            if (Array.isArray(data)) return data;
            const record = data as Record<string, unknown> | undefined;
            if (record && "items" in record && Array.isArray(record.items))
              return record.items;
            if (record && "data" in record && Array.isArray(record.data))
              return record.data;
            return [];
          },
        });

        if ("failure" in result) break;

        const rawBody = result.success.body as Record<string, unknown> | undefined;
        const items = (result.success.data || []) as SolicitacaoAgente[];

        if (items.length === 0) break;
        allItems.push(...items);

        const totalCount =
          typeof rawBody?.totalCount === "number"
            ? rawBody.totalCount
            : typeof rawBody?.total === "number"
              ? rawBody.total
              : typeof rawBody?.totalRecords === "number"
                ? rawBody.totalRecords
                : typeof rawBody?.count === "number"
                  ? rawBody.count
                  : undefined;

        if (typeof totalCount === "number" && allItems.length >= totalCount) break;
        if (items.length < pageSize) break;

        page++;
      }

      setSolicitacoes(allItems);
    } catch (err) {
      // Error fetching agent requests
    } finally {
      setIsLoadingSolicitacoes(false);
    }
  };

  const fetchOuvidoria = async () => {
    if (!AppConfig.ouvidoriaId || !AppConfig.municipioId) return;
    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      const endpoint = `v1/municipios/${AppConfig.municipioId}/ouvidorias/${AppConfig.ouvidoriaId}`;
      
      const result = await dataSource.get<Record<string, unknown>>({
        endpoint,
        headers: { Accept: "application/vnd.data.full.v1+json" },
        forceRefresh: true,
      });

      if ("failure" in result) return;
      const raw = result.success.body as Record<string, unknown>;
      
      if (raw && raw.nome) {
        setOuvidoriaNome(String(raw.nome));
      }
    } catch (err) {
      // Error fetching ouvidoria
    }
  };

  useEffect(() => {
    fetchAgente();
    fetchSolicitacoes();
    fetchOuvidoria();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agenteId]);

  // DESABILITADO TEMPORARIAMENTE: refresh on focus/visibility estava causando
  // problemas de reatividade (sobrescrevendo estados ao selecionar arquivos, etc).
  // useEffect(() => {
  //   const handleFocus = () => {
  //     fetchAgente();
  //     fetchSolicitacoes();
  //   };
  //
  //   const handleVisibility = () => {
  //     if (document.visibilityState === 'visible') {
  //       fetchAgente();
  //       fetchSolicitacoes();
  //     }
  //   };
  //
  //   window.addEventListener('focus', handleFocus);
  //   document.addEventListener('visibilitychange', handleVisibility);
  //
  //   return () => {
  //     window.removeEventListener('focus', handleFocus);
  //     document.removeEventListener('visibilitychange', handleVisibility);
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [agenteId]);

  // Contagens seguindo a mesma lógica de SolicitacoesPage:
  //   - Pendente / EmAnalise / Agendada / Resolvida → status + estadoAprovacao = Aprovada
  //   - Aguardando Aprovação / Reprovada → apenas estadoAprovacao
  const stats = useMemo(() => {
    const aprovada = normalizeValue("Aprovada");
    const isAprovada = (s: SolicitacaoAgente) =>
      normalizeValue(s.estadoAprovacao) === aprovada;
    const byStatus = (s: SolicitacaoAgente, alvo: string) =>
      normalizeValue(s.status) === normalizeValue(alvo) && isAprovada(s);

    return {
      total: solicitacoes.length,
      pendentes: solicitacoes.filter((s) => byStatus(s, "Pendente")).length,
      emAnalise: solicitacoes.filter((s) => byStatus(s, "Em análise")).length,
      agendadas: solicitacoes.filter((s) => byStatus(s, "Agendada")).length,
      resolvidas: solicitacoes.filter((s) => byStatus(s, "Resolvida")).length,
      aguardando: solicitacoes.filter(
        (s) =>
          normalizeValue(s.estadoAprovacao) ===
          normalizeValue("Aguardando Aprovação"),
      ).length,
      reprovadas: solicitacoes.filter(
        (s) => normalizeValue(s.estadoAprovacao) === normalizeValue("Reprovada"),
      ).length,
    };
  }, [solicitacoes]);

  const handleFotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      setEditForm((prev) => ({ ...prev, fotoPerfil: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!agenteId) return;

    setIsSaving(true);
    try {
      const token = AppConfig.tokenAcesso;

      const fotoChanged =
        editForm.fotoPerfil !== (agente?.fotoPerfil?.arquivo || "");
      if (
        fotoChanged &&
        editForm.fotoPerfil &&
        editForm.fotoPerfil.startsWith("data:")
      ) {
        try {
          const fotoFormData = new FormData();
          const base64Data = editForm.fotoPerfil.split(",")[1];
          const mimeType =
            editForm.fotoPerfil.split(",")[0].match(/:(.*?);/)?.[1] ||
            "image/png";
          const byteCharacters = atob(base64Data);
          const byteArrays = [];
          const sliceSize = 512;

          for (
            let offset = 0;
            offset < byteCharacters.length;
            offset += sliceSize
          ) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
              byteNumbers[i] = slice.charCodeAt(i);
            }
            byteArrays.push(new Uint8Array(byteNumbers));
          }

          const blob = new Blob(byteArrays, { type: mimeType });
          fotoFormData.append("arquivo", blob, "foto.png");
          fotoFormData.append("date", String(Date.now()));

          const fotoUrl = `${Settings.urlBase}/v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes/${agenteId}/atualizar-foto-perfil`;

          const fotoResponse = await fetch(fotoUrl, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: fotoFormData,
          });

          if (!fotoResponse.ok) {
            const errorData = await fotoResponse.json().catch(() => ({}));
            throw new Error(
              errorData.message ||
                errorData.detail ||
                `Erro ${fotoResponse.status}: ${fotoResponse.statusText}`
            );
          }

          // Lê a resposta do servidor para obter a URL atualizada da foto
          const fotoResult = await fotoResponse.json().catch(() => ({})) as Record<string, unknown>;
          const novaUrl = (fotoResult.arquivo || fotoResult.url || fotoResult.fotoUrl) as string | undefined;
          if (novaUrl) {
            setAgente((prev) =>
              prev
                ? {
                    ...prev,
                    fotoPerfil: {
                      id: (fotoResult.id as string) || prev.fotoPerfil?.id || "",
                      arquivo: novaUrl,
                    },
                  }
                : prev
            );
            setEditForm((prev) => ({ ...prev, fotoPerfil: novaUrl }));
          }
          setFotoVersion(Date.now());
        } catch (error) {
          throw new Error(
            "Erro ao atualizar a foto de perfil: " +
              (error instanceof Error ? error.message : "Erro desconhecido")
          );
        }
      }

      type JsonPatchOp = { op: "replace"; path: string; value: unknown };
      const operations: JsonPatchOp[] = [];

      if (editForm.nome !== (agente?.nome || "")) {
        operations.push({ op: "replace", path: "/nome", value: editForm.nome });
      }
      if (editForm.email !== (agente?.email || "")) {
        operations.push({
          op: "replace",
          path: "/email",
          value: editForm.email || null,
        });
      }
      if (editForm.telefone !== (agente?.telefone || "")) {
        operations.push({
          op: "replace",
          path: "/telefone",
          value: editForm.telefone || null,
        });
      }

      if (operations.length === 0 && !fotoChanged) {
        toast({
          title: "Nada para salvar",
          description: "Nenhuma alteração foi feita.",
        });
        setIsSaving(false);
        return;
      }

      if (operations.length > 0) {
        const dataSource = new RemoteDataSource(Settings.urlBase);
        const result = await dataSource.patch({
          endpoint: OUVIDORIA_ENDPOINTS.atualizarAgente(agenteId),
          headers: { "content-type": "application/json-patch+json" },
          body: operations,
        });

        if ("failure" in result) {
          toast({
            title: "Erro ao atualizar",
            description:
              result.failure.messages.join(", ") ||
              "Erro ao atualizar perfil",
            variant: "destructive",
          });
          return;
        }
      }

      toast({
        title: "Perfil atualizado",
        description: "Seus dados foram salvos com sucesso.",
      });

      setIsEditing(false);
      await fetchAgente();
      // Atualiza o store global para refletir na sidebar/app bar
      await fetchCurrentAgente(true);
    } catch (err) {
      toast({
        title: "Erro ao atualizar",
        description:
          err instanceof Error ? err.message : "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const avatarFallback = agente?.nome
    ? agente.nome
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() || "")
        .join("") || "EU"
    : "EU";
  const avatarSrc = (() => {
    if (isEditing) {
      return editForm.fotoPerfil.startsWith("data:")
        ? editForm.fotoPerfil
        : (resolveMediaUrl(editForm.fotoPerfil) || editForm.fotoPerfil);
    }
    return resolveMediaUrl(agente?.fotoPerfil?.arquivo);
  })();
  const avatarBlobUrl = useImageBlobUrl(avatarSrc);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando seu perfil...</p>
        </div>
      </div>
    );
  }

  if (error || !agente) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center space-y-4">
            <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <div className="space-y-1">
              <h3 className="text-xl font-semibold text-foreground">
                Perfil indisponível
              </h3>
              <p className="text-muted-foreground max-w-md">
                {error || "Não foi possível carregar seus dados."}
              </p>
            </div>
            <Button variant="outline" onClick={fetchAgente}>
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Hero / Cover */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="relative overflow-hidden rounded-2xl border border-border"
      >
        <div className="h-40 sm:h-48 bg-gradient-to-br from-primary via-primary/80 to-secondary relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.18),_transparent_60%)]" />
          <div className="absolute inset-0 opacity-20 mix-blend-overlay bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22 viewBox=%220 0 40 40%22><g fill=%22none%22 stroke=%22%23fff%22 stroke-opacity=%220.3%22 stroke-width=%221%22><path d=%22M0 20h40M20 0v40%22/></g></svg>')]" />
        </div>

        <div className="bg-card px-4 sm:px-8 pb-6 pt-0">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 -mt-12 sm:-mt-16">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div className="relative self-start">
                <div className="rounded-full ring-4 ring-card shadow-lg">
                  <Avatar className="h-24 w-24 sm:h-32 sm:w-32">
                    {avatarBlobUrl ? (
                      <AvatarImage
                        key={`${avatarBlobUrl}@${fotoVersion}`}
                        src={avatarBlobUrl}
                        alt={agente.nome}
                        onError={(e) => {
                          console.error("[PerfilPage] AvatarImage error:", e.currentTarget.src);
                        }}
                        onLoad={() => {
                          console.log("[PerfilPage] AvatarImage loaded");
                        }}
                      />
                    ) : null}
                    <AvatarFallback className="text-3xl bg-gradient-to-br from-primary/20 to-secondary/20 text-primary font-heading font-semibold">
                      {avatarFallback}
                    </AvatarFallback>
                  </Avatar>
                </div>
                {isEditing && (
                  <>
                    <label
                      htmlFor="foto-upload"
                      className="absolute bottom-1 right-1 h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors shadow-lg ring-2 ring-card"
                    >
                      <Camera className="h-4 w-4" />
                    </label>
                    <input
                      id="foto-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFotoUpload}
                    />
                  </>
                )}
              </div>

              <div className="space-y-1 sm:pb-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-heading text-2xl sm:text-3xl font-bold">
                    {agente.nome || "Agente"}
                  </h1>
                  <Badge
                    variant={agente.arquivado ? "destructive" : "default"}
                    className={cn(
                      !agente.arquivado &&
                        "bg-success/10 text-success border-success/20 hover:bg-success/15"
                    )}
                  >
                    {agente.arquivado ? (
                      <>
                        <X className="h-3 w-3 mr-1" />
                        Arquivado
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Ativo
                      </>
                    )}
                  </Badge>
                </div>
              </div>
            </div>

            {!isEditing && !isReadOnly && (
              <div className="flex items-center gap-2 sm:pb-2">
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit2 className="h-4 w-4" />
                  Editar perfil
                </Button>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* KPIs (status × estadoAprovacao, igual SolicitacoesPage) */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.05 }}
        className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6"
      >
        <KpiCard
          icon={Clock}
          label="Pendentes"
          value={stats.pendentes}
          accent="primary"
          loading={isLoadingSolicitacoes}
        />
        <KpiCard
          icon={ClipboardList}
          label="Em análise"
          value={stats.emAnalise}
          accent="warning"
          loading={isLoadingSolicitacoes}
        />
        <KpiCard
          icon={CheckCircle2}
          label="Agendadas"
          value={stats.agendadas}
          accent="lime"
          loading={isLoadingSolicitacoes}
        />
        <KpiCard
          icon={CheckCircle2}
          label="Resolvidas"
          value={stats.resolvidas}
          accent="success"
          loading={isLoadingSolicitacoes}
        />
        <KpiCard
          icon={Clock}
          label="Aguardando"
          value={stats.aguardando}
          accent="amber"
          loading={isLoadingSolicitacoes}
        />
        <KpiCard
          icon={XCircle}
          label="Reprovadas"
          value={stats.reprovadas}
          accent="destructive"
          loading={isLoadingSolicitacoes}
        />
      </motion.div>

      {/* Main grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column: dados / edição */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1 }}
          className="lg:col-span-2 space-y-6"
        >
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle>
                  {isEditing ? "Editar informações" : "Informações pessoais"}
                </CardTitle>
                <CardDescription>
                  {isEditing
                    ? "Atualize seus dados de contato e foto de perfil"
                    : "Como você aparece para os cidadãos e demais agentes"}
                </CardDescription>
              </div>
              {isEditing ? null : (
                <Badge variant="outline" className="gap-1">
                  <Shield className="h-3 w-3" />
                  Privado
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome completo</Label>
                    <Input
                      id="nome"
                      value={editForm.nome}
                      onChange={(e) =>
                        setEditForm({ ...editForm, nome: e.target.value })
                      }
                      placeholder="Seu nome"
                    />
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          value={editForm.email}
                          onChange={(e) =>
                            setEditForm({ ...editForm, email: e.target.value })
                          }
                          placeholder="email@exemplo.com"
                          className="pl-9"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="telefone"
                          value={editForm.telefone}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              telefone: e.target.value,
                            })
                          }
                          placeholder="(11) 99999-9999"
                          className="pl-9"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-dashed border-border bg-surface-container/40 p-4 flex items-start gap-3">
                    <Upload className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-foreground">
                        Foto de perfil
                      </p>
                      <p className="text-muted-foreground">
                        Use o ícone de câmera sobre o avatar acima para enviar
                        uma nova foto.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEditing(false);
                        setEditForm({
                          nome: agente.nome || "",
                          email: agente.email || "",
                          telefone: agente.telefone || "",
                          fotoPerfil: agente.fotoPerfil?.arquivo || "",
                        });
                      }}
                      disabled={isSaving}
                      className="gap-2"
                    >
                      <X className="h-4 w-4" />
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="gap-2"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          Salvar alterações
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <InfoRow
                    icon={Mail}
                    label="E-mail"
                    value={agente.email || "—"}
                  />
                  <InfoRow
                    icon={Phone}
                    label="Telefone"
                    value={agente.telefone || "—"}
                  />
                  <InfoRow
                    icon={Shield}
                    label="Status"
                    value={agente.arquivado ? "Arquivado" : "Ativo"}
                  />
                  <InfoRow
                    icon={LinkIcon}
                    label="Integração"
                    value={agente.intergracao || "—"}
                  />
                </div>
              )}
            </CardContent>
          </Card>

        </motion.div>

        {/* Right column: side cards */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15 }}
          className="space-y-6"
        >
          {/* Desempenho */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Desempenho
              </CardTitle>
              <CardDescription>
                Sua taxa de resolução e fluxo de aprovação
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ProgressMetric
                label="Taxa de resolução"
                value={stats.resolvidas}
                total={stats.total}
                color="success"
              />
              <ProgressMetric
                label="Em análise"
                value={stats.emAnalise}
                total={stats.total}
                color="warning"
              />
              <ProgressMetric
                label="Aguardando aprovação"
                value={stats.aguardando}
                total={stats.total}
                color="warning"
              />
              <Separator />
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total atribuídas</span>
                <span className="font-semibold">{stats.total}</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}

type KpiAccent =
  | "primary"
  | "success"
  | "warning"
  | "amber"
  | "lime"
  | "destructive";

function KpiCard({
  icon: Icon,
  label,
  value,
  accent,
  loading,
}: {
  icon: typeof Clock;
  label: string;
  value: number;
  accent: KpiAccent;
  loading?: boolean;
}) {
  const accentClasses: Record<KpiAccent, string> = {
    primary: "bg-primary/10 text-primary",
    success: "bg-success/10 text-success",
    warning: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    lime: "bg-lime-500/10 text-lime-700 dark:text-lime-400",
    destructive: "bg-destructive/10 text-destructive",
  };
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
              {label}
            </p>
            <p className="text-2xl font-heading font-bold">
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : (
                value
              )}
            </p>
          </div>
          <div
            className={cn(
              "h-10 w-10 rounded-lg flex items-center justify-center",
              accentClasses[accent]
            )}
          >
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Mail;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium break-all">{value}</p>
      </div>
    </div>
  );
}

function ProgressMetric({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: "success" | "warning";
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const barColor =
    color === "success" ? "bg-success" : "bg-amber-500 dark:bg-amber-400";
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {value}
          <span className="text-muted-foreground"> / {total}</span>
          <span className="ml-2 text-xs text-muted-foreground">({pct}%)</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={cn("h-full transition-all", barColor)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
